// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { groupPositionsByAssetType } from './group-positions-by-asset-type';
import type { ThunkDispatch } from '@reduxjs/toolkit';

export type AssetsInfoBundle = {
  readonly assets: AssetsInfoResp[];
  readonly classifierByTicker: ReadonlyMap<string, string>;
};

export const getAssetsInfoBundle = async (
  tickers: string[],
  dispatch: ThunkDispatch<any, any, any>,
): Promise<AssetsInfoBundle> => {
  const action = assetsApi.endpoints.classifier.initiate({ payload: tickers });
  const response = await dispatch(action).unwrap();
  const classifierByTicker = new Map(
    response.map(({ ticker, asset_type }) => [ticker, asset_type]),
  );
  const { other: _, ...assetTypes } = groupPositionsByAssetType(response);

  const requests = Object.keys(assetTypes).map(assetType => {
    const tickers = assetTypes[assetType as keyof typeof assetTypes].map(
      ({ ticker }) => ticker,
    );
    const action = assetsApi.endpoints.assetsInfo.initiate({
      params: { detail: true },
      payload: assetTypes[assetType as keyof typeof assetTypes].map(
        ({ ticker }) => ticker,
      ),
      assetType,
    });

    return tickers.length
      ? dispatch(action)
          .unwrap()
          .then(data =>
            data.map(item => ({
              ...item,
              asset_type: assetType,
            })),
          )
      : [];
  });

  const assetsInfo = await Promise.all(requests);

  return {
    assets: assetsInfo.flat(),
    classifierByTicker,
  };
};

export const getAssetsInfo = async (
  tickers: string[],
  dispatch: ThunkDispatch<any, any, any>,
): Promise<AssetsInfoResp[]> => {
  const { assets } = await getAssetsInfoBundle(tickers, dispatch);

  return assets;
};
